import { useState } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { adminLogout } from '../api/admin'
import { useAdminSession } from './session'
import { Button } from '../components/ui/Button'

// Lightweight equivalent of the applicant app's PageShell/NavBar: same glass
// header treatment and design tokens, but deliberately no motion/animation —
// this is an internal ops tool, not the applicant-facing brand surface.
export function AdminShell({ children }: { children: ReactNode }) {
  const { user } = useAdminSession()
  // Blanks the screen the instant Log out is clicked, before the async
  // adminLogout() call even starts — otherwise the current page's data stays
  // fully visible for the whole network round-trip + page reload, which
  // reads as "still logged in" for a beat after the click.
  const [loggingOut, setLoggingOut] = useState(false)

  async function handleLogout() {
    setLoggingOut(true)
    try {
      await adminLogout()
    } catch {
      // Logout is idempotent server-side; drop the client-side session regardless.
    }
    window.location.assign('/admin/login')
  }

  if (loggingOut) {
    return <div style={{ minHeight: '100svh', background: 'var(--color-surface-base)' }} />
  }

  return (
    <div
      className="flex flex-col"
      style={{ minHeight: '100svh', background: 'var(--color-surface-base)' }}
    >
      <header
        className="sticky top-0 flex items-center justify-between gap-6 px-6 md:px-8"
        style={{
          height: '64px',
          zIndex: 10,
          background: 'var(--color-surface-glass)',
          backdropFilter: 'var(--glass-standard-blur)',
          WebkitBackdropFilter: 'var(--glass-standard-blur)',
          borderBottom: '1px solid var(--color-outline-hairline)',
        }}
      >
        <div className="flex items-center gap-6">
          <img
            src={`${import.meta.env.BASE_URL}zota-logo.svg`}
            alt="Zota"
            style={{ height: '20px', width: 'auto', display: 'block' }}
          />
          <nav className="flex items-center gap-1" style={{ fontFamily: 'var(--font-body)' }}>
            <AdminNavLink to="/accounts">Accounts</AdminNavLink>
            <AdminNavLink to="/applications">Applications</AdminNavLink>
            <AdminNavLink to="/settings">Settings</AdminNavLink>
            {user?.role === 'admin' && <AdminNavLink to="/docai-test">DocAI Test</AdminNavLink>}
          </nav>
        </div>
        {user && (
          <div className="flex items-center gap-4" style={{ fontFamily: 'var(--font-body)' }}>
            <span
              className="hidden sm:block"
              style={{
                fontSize: 'var(--text-body-sm)',
                color: 'var(--color-text-secondary)',
                maxWidth: '200px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={user.email}
            >
              {user.email}
            </span>
            <Button variant="tertiary" onClick={handleLogout}>
              Log out
            </Button>
          </div>
        )}
      </header>
      <main
        className="flex-1 mx-auto w-full"
        style={{
          maxWidth: '1200px',
          paddingLeft: 'var(--page-padding-horizontal)',
          paddingRight: 'var(--page-padding-horizontal)',
          paddingTop: 'var(--spacing-8)',
          paddingBottom: 'var(--spacing-10)',
        }}
      >
        {children}
      </main>
    </div>
  )
}

function AdminNavLink({ to, children }: { to: string; children: string }): ReactElement {
  return (
    <NavLink
      to={to}
      style={({ isActive }) => ({
        padding: '8px 14px',
        borderRadius: 'var(--radius-full)',
        fontSize: 'var(--text-body-sm)',
        fontWeight: 'var(--weight-semibold)',
        color: isActive ? 'var(--color-text-on-primary)' : 'var(--color-text-secondary)',
        background: isActive ? 'var(--color-action-primary)' : 'transparent',
        textDecoration: 'none',
      })}
    >
      {children}
    </NavLink>
  )
}
